/**
 * AI Utility Functions for SERP Analyzer Pro
 * Handles API calls to Anthropic, OpenAI, and Google Gemini
 */

/**
 * Fetches available models from the specified provider
 * @param {string} provider - 'anthropic', 'openai', or 'google'
 * @param {string} apiKey - The API key for the provider
 * @returns {Promise<string[]>} List of model IDs
 */
export const fetchAiModels = async (provider, apiKey) => {
    if (!apiKey) throw new Error(`Chiave API ${provider} mancante`);

    try {
        if (provider === 'anthropic') {
            const response = await fetch("https://api.anthropic.com/v1/models", {
                headers: {
                    "x-api-key": apiKey,
                    "anthropic-version": "2023-06-01",
                    "dangerouslyAllowBrowser": "true"
                }
            });
            if (!response.ok) throw new Error(`Errore Anthropic: ${response.statusText}`);
            const data = await response.json();
            return data.data.map(m => m.id);

        } else if (provider === 'openai') {
            const response = await fetch("https://api.openai.com/v1/models", {
                headers: {
                    "Authorization": `Bearer ${apiKey}`
                }
            });
            if (!response.ok) throw new Error(`Errore OpenAI: ${response.statusText}`);
            const data = await response.json();
            return data.data
                .filter(m => m.id.includes('gpt') || m.id.includes('o1'))
                .map(m => m.id)
                .sort();

        } else if (provider === 'google') {
            const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`);
            if (!response.ok) throw new Error(`Errore Google: ${response.statusText}`);
            const data = await response.json();
            return data.models
                .filter(m => m.supportedGenerationMethods.includes('generateContent'))
                .map(m => m.name.replace('models/', ''));
        }
    } catch (error) {
        console.error(`Error fetching ${provider} models:`, error);
        throw error;
    }
    return [];
};

/**
 * Generates SEO insights using the selected AI provider
 * @param {Object} analysisData - Transformed SERP data
 * @param {Object} aiConfig - AI configuration (provider, model, keys)
 * @param {string} clientDomain - Optional client domain for context
 * @returns {Promise<Object>} Parsed JSON insights
 */
export const generateAiInsights = async (analysisData, aiConfig, clientDomain) => {
    const provider = aiConfig.provider;
    const model = aiConfig.model;
    const apiKey = aiConfig.apiKeys[provider];

    if (!apiKey) throw new Error(`Chiave API per ${provider} mancante`);

    // Estrarre i dati strutturati per il prompt
    const {
        resultTypes,
        serpDifficulty,
        priceData,
        topDomains,
        relatedSearches,
        paaQuestions,
        keyword
    } = analysisData;

    // Costruzione del prompt esperto
    const prompt = `Sei un Consulente SEO Senior di fama mondiale, esperto in analisi SERP e strategia di contenuto.
Il tuo obiettivo è analizzare i dati forniti per la keyword "${keyword}" e fornire una strategia vincente.

=== 1. DATI DELLA SERP (Analizzali con cura) ===
- PANORAMICA VISUALE: ${JSON.stringify(resultTypes)}
  (Usa questi dati per capire quali formati Google preferisce ora: Video? Immagini? Shopping?)
- DIFFICOLTÀ SEO: ${JSON.stringify(serpDifficulty)}
  (Score, Features presenti, % Organica. Quanto è difficile entrare?)
- INTENTO & PREZZI: ${JSON.stringify(priceData.slice(0, 5))}
  (Se ci sono prezzi, l'intento è transazionale. Usalo per calibrare la strategia.)
- COMPETITOR TOP 10: ${JSON.stringify(topDomains)}
  (Chi domina? Brand giganti o siti di nicchia? C'è spazio?)
- OPPORTUNITÀ SEMANTICHE:
  * Ricerche Correlate: ${JSON.stringify(relatedSearches.slice(0, 10))}
  * Domande Frequenti (PAA): ${JSON.stringify(paaQuestions.slice(0, 10))}
  (Usa questi per trovare gap semantici e buchi nei contenuti dei competitor.)

=== 2. CONTESTO CLIENTE ===
DOMINIO CLIENTE: ${clientDomain ? clientDomain : "NESSUNO (Strategia per nuovo player)"}
${clientDomain ? `ISTRUZIONE IMPORTANTE: Il tuo compito è dare consigli attuali. 
Tenta di VISITARE/NAVIGARE il sito "${clientDomain}" in tempo reale per analizzarne lo stato odierno.
NON basarti su dati storici o sulla tua memoria a lungo termine, a meno che la navigazione live non sia tecnicamente impossibile per te.
Devi capire il gap tra lo stato ATTUALE del sito e i competitor in SERP.` : ""}

=== 3. LINEE GUIDA OUTPUT ===
- Tono: Professionale, Diretto, Esperto. EVITA EMOJI eccessive.
- Formattazione: Puoi usare markdown H3, Quote, Cite e punti elenco per suddividere concetti e paragrafi se necessario. Puoi usare **grassetto** per concetti chiave e *corsivo* per termini tecnici.
- Approccio: I primi 4 insight devono analizzare la SERP. Gli ultimi 2 (Action Items/Quick Wins) devono essere su misura per il CLIENTE (se presente) o generici (se assente).

=== 4. FORMATO RICHIESTO (JSON STRICT) ===
Rispondi ESCLUSIVAMENTE con un oggetto JSON valido. Niente testo prima o dopo.
{
  "serpFeatures": "Analisi visuale della SERP. Quali formati dominano? Perché? Usa grassetti.",
  "competitiveness": "Analisi della difficoltà. Chi sono i big player? C'è spazio? Usa grassetti.",
  "searchIntent": "Analisi dell'intento (Info/Nav/Trans). Cosa vuole davvero l'utente?",
  "contentStrategy": "Strategia editoriale pratica. Che tipo di pagine creare? Che taglio dare?",
  "actionItems": ["Azione pratica 1", "Azione pratica 2", ...],
  "quickWins": ["Vittoria rapida 1", "Vittoria rapida 2", ...]
}
Genera 4-6 action items e 2-4 quick wins concreti.`;

    let insightsText = '';

    if (provider === 'anthropic') {
        const response = await fetch("https://api.anthropic.com/v1/messages", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "x-api-key": apiKey,
                "anthropic-version": "2023-06-01",
                "dangerouslyAllowBrowser": "true"
            },
            body: JSON.stringify({
                model: model,
                max_tokens: 1500,
                temperature: 0.2,
                messages: [{ role: "user", content: prompt }]
            })
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Errore Anthropic: ${errorData.error?.message || response.statusText}`);
        }
        const result = await response.json();
        insightsText = result.content[0].text;

    } else if (provider === 'openai') {
        const response = await fetch("https://api.openai.com/v1/chat/completions", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${apiKey}`
            },
            body: JSON.stringify({
                model: model,
                messages: [{ role: "user", content: prompt }],
                temperature: 0.2
            })
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Errore OpenAI: ${errorData.error?.message || response.statusText}`);
        }
        const result = await response.json();
        insightsText = result.choices[0].message.content;

    } else if (provider === 'google') {
        const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                contents: [{ parts: [{ text: prompt }] }],
                generationConfig: { temperature: 0.2 }
            })
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Errore Google: ${errorData.error?.message || response.statusText}`);
        }
        const result = await response.json();
        insightsText = result.candidates[0].content.parts[0].text;
    }

    // Robust cleaning of JSON response
    const jsonMatch = insightsText.match(/\{[\s\S]*\}/);
    if (!jsonMatch) throw new Error("L'AI non ha restituito un JSON valido. Riprova.");

    return JSON.parse(jsonMatch[0]);
};

/**
 * Sends a direct prompt to the AI provider and returns the raw text response.
 * Used for simulating AI Chat searches (GEO).
 */
export const askAiDirectly = async (provider, model, apiKey, prompt) => {
    if (!apiKey) throw new Error(`Chiave API per ${provider} mancante`);

    try {
        if (provider === 'anthropic') {
            const response = await fetch("https://api.anthropic.com/v1/messages", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-api-key": apiKey,
                    "anthropic-version": "2023-06-01",
                    "dangerouslyAllowBrowser": "true"
                },
                body: JSON.stringify({
                    model: model,
                    max_tokens: 1000,
                    temperature: 0.7,
                    messages: [{ role: "user", content: prompt }]
                })
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error?.message || response.statusText);
            }
            const data = await response.json();
            return data.content[0].text;

        } else if (provider === 'openai') {
            const response = await fetch("https://api.openai.com/v1/chat/completions", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${apiKey}`
                },
                body: JSON.stringify({
                    model: model,
                    messages: [{ role: "user", content: prompt }],
                    temperature: 0.7
                })
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error?.message || response.statusText);
            }
            const data = await response.json();
            return data.choices[0].message.content;

        } else if (provider === 'google') {
            const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    contents: [{ parts: [{ text: prompt }] }]
                })
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error?.message || response.statusText);
            }
            const data = await response.json();
            return data.candidates[0].content.parts[0].text;
        }
    } catch (error) {
        console.error("AI Direct Request Error:", error);
        throw error;
    }
};
